import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { formatNumber, formatEuro } from "@/lib/utils";

/* ═══════════════════════════ Constants ═══════════════════════════ */

const QUICK_FILTERS = [
  { key: "week", label: "Diese Woche" },
  { key: "4weeks", label: "4 Wo." },
  { key: "8weeks", label: "8 Wo." },
  { key: "12weeks", label: "12 Wo." },
  { key: "month", label: "Monat" },
  { key: "lastmonth", label: "Letzter M." },
  { key: "quarter", label: "Quartal" },
  { key: "year", label: "Jahr" },
  { key: "all", label: "Alles" },
  { key: "custom", label: "Benutzerdefiniert" },
];

const DISPLAY_MODES = [
  { key: "auto", label: "Auto" },
  { key: "days", label: "Tage" },
  { key: "weeks", label: "Wochen" },
  { key: "months", label: "Monate" },
  { key: "quarters", label: "Quartale" },
  { key: "years", label: "Jahre" },
];

const PERIOD_PREFIX = { days: "Tages", weeks: "Wochen", months: "Monats", quarters: "Quartals", years: "Jahres" };
const PERIOD_PER = { days: "pro Tag", weeks: "pro Woche", months: "pro Monat", quarters: "pro Quartal", years: "pro Jahr" };
const DISPLAY_LABELS = { days: "Tage", weeks: "Wochen", months: "Monate", quarters: "Quartale", years: "Jahre" };
const MONTHS_SHORT = ["Jan", "Feb", "Maer", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/* ═══════════════════════════ Date Helpers ═══════════════════════════ */

function toStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getFilterRange(mode, cFrom, cTo) {
  const t = new Date();
  const ts = toStr(t);
  switch (mode) {
    case "week": {
      const day = t.getDay();
      const mon = new Date(t);
      mon.setDate(t.getDate() - (day === 0 ? 6 : day - 1));
      return { from: toStr(mon), to: ts };
    }
    case "4weeks": { const d = new Date(t); d.setDate(d.getDate() - 27); return { from: toStr(d), to: ts }; }
    case "8weeks": { const d = new Date(t); d.setDate(d.getDate() - 55); return { from: toStr(d), to: ts }; }
    case "12weeks": { const d = new Date(t); d.setDate(d.getDate() - 83); return { from: toStr(d), to: ts }; }
    case "month":
      return { from: `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-01`, to: ts };
    case "lastmonth": {
      const f = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const l = new Date(t.getFullYear(), t.getMonth(), 0);
      return { from: toStr(f), to: toStr(l) };
    }
    case "quarter": {
      const q = Math.floor(t.getMonth() / 3);
      return { from: toStr(new Date(t.getFullYear(), q * 3, 1)), to: ts };
    }
    case "year": return { from: `${t.getFullYear()}-01-01`, to: ts };
    case "all": return { from: "2020-01-01", to: ts };
    case "custom": return { from: cFrom || ts, to: cTo || ts };
    default: return { from: `${t.getFullYear()}-01-01`, to: ts };
  }
}

function getAutoDisplay(from, to) {
  const days = Math.round((new Date(to) - new Date(from)) / 86400000);
  if (days <= 60) return "days";
  if (days <= 90) return "weeks";
  if (days <= 365) return "months";
  if (days <= 730) return "quarters";
  return "years";
}

function formatDateDE(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDate();
  const months = ["Januar", "Februar", "Maerz", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
  return `${day}. ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/* ═══════════════════════════ Aggregation ═══════════════════════════ */

function getGroupKey(dateStr, mode) {
  const d = new Date(dateStr + "T00:00:00");
  switch (mode) {
    case "days": return dateStr;
    case "weeks": {
      const thu = new Date(d);
      thu.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
      const y1 = new Date(thu.getFullYear(), 0, 1);
      const wn = Math.ceil((((thu - y1) / 86400000) + 1) / 7);
      return `${thu.getFullYear()}-W${String(wn).padStart(2, "0")}`;
    }
    case "months":
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    case "quarters":
      return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
    case "years":
      return `${d.getFullYear()}`;
    default: return dateStr;
  }
}

function formatGroupLabel(key, mode) {
  switch (mode) {
    case "days": {
      const [, m, d] = key.split("-");
      return `${d}.${m}.`;
    }
    case "weeks": return `KW${parseInt(key.split("-W")[1])}`;
    case "months": {
      const [y, m] = key.split("-");
      return `${MONTHS_SHORT[parseInt(m) - 1]} ${y.slice(2)}`;
    }
    case "quarters": {
      const parts = key.split("-");
      return `${parts[1]}/${parts[0].slice(2)}`;
    }
    case "years": return key;
    default: return key;
  }
}

function aggregateData(entries, mode, euroRate) {
  if (!entries.length) return [];
  const groups = {};
  entries.forEach((e) => {
    const k = getGroupKey(e.date, mode);
    if (!groups[k]) groups[k] = { key: k, list: [] };
    groups[k].list.push(e);
  });
  return Object.values(groups)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((g) => {
      const tp = g.list.reduce((s, e) => s + (e.time_points || 0), 0);
      const sp = g.list.reduce((s, e) => s + (e.sales_points || 0), 0);
      let prod = 0, admin = 0, akq = 0;
      g.list.forEach((e) => {
        const t = e.time_entries || {};
        prod += t.produktion || 0;
        admin += t.administration || 0;
        akq += (t.recherche || 0) + (t.cold_calls || 0);
      });
      return {
        label: formatGroupLabel(g.key, mode),
        Zeit: Math.round(tp * 10) / 10,
        Vertrieb: Math.round(sp * 10) / 10,
        Punkte: Math.round((tp + sp) * 10) / 10,
        Produktion: Math.round(prod * 10) / 10,
        Administration: Math.round(admin * 10) / 10,
        Akquise: Math.round(akq * 10) / 10,
        euro: Math.round((tp + sp) * euroRate * 100) / 100,
      };
    });
}

/* ═══════════════════════════ Tooltips ═══════════════════════════ */

const PtsTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-mono">
          {p.name}: {formatNumber(p.value)} Pkt.
        </p>
      ))}
    </div>
  );
};

const HoursTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-mono">
          {p.name}: {formatNumber(p.value)} h
        </p>
      ))}
      <p className="font-mono text-white border-t border-zinc-800 mt-1 pt-1">
        Gesamt: {formatNumber(Math.round(total * 10) / 10)} h
      </p>
    </div>
  );
};

const EuroTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-mono text-emerald-400">{formatEuro(p.value)}</p>
      ))}
    </div>
  );
};

const CatTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs">
      <p style={{ color: d.payload.color }} className="font-mono">
        {d.name}: {formatNumber(d.value)} h
      </p>
    </div>
  );
};

/* ═══════════════════════════ Heatmap ═══════════════════════════ */

const HC = [
  "#0A0A0A", "#0F2B38", "#0C3E52", "#0E5268",
  "#0E6E88", "#0891B2", "#06B6D4", "#22D3EE", "#67E8F9",
];

function heatColor(h) {
  if (!h || h === 0) return HC[0];
  if (h < 0.5) return HC[1];
  if (h < 1.5) return HC[2];
  if (h < 2.5) return HC[3];
  if (h < 4) return HC[4];
  if (h < 6) return HC[5];
  if (h < 8) return HC[6];
  if (h < 10) return HC[7];
  return HC[8];
}

function Heatmap({ data, from, to }) {
  const [tip, setTip] = useState(null);
  if (from > to) return null;
  const start = new Date(from + "T00:00:00");
  const end = new Date(to + "T00:00:00");
  const day = start.getDay();
  start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));

  const weeks = [];
  let week = [];
  const cur = new Date(start);
  while (cur <= end) {
    const ds = toStr(cur);
    const inR = ds >= from && ds <= to;
    week.push(inR ? { date: ds, total: data[ds]?.total || 0, work: data[ds]?.work || 0 } : null);
    if (week.length === 7) { weeks.push(week); week = []; }
    cur.setDate(cur.getDate() + 1);
  }
  if (week.length) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }
  if (!weeks.length) return null;

  return (
    <div className="overflow-x-auto relative">
      {tip && (
        <div
          className="fixed z-50 bg-zinc-900 border border-zinc-700 px-3 py-2 text-xs pointer-events-none shadow-lg"
          style={{ left: tip.x + 14, top: tip.y - 70 }}
        >
          <p className="text-zinc-300 mb-1">{formatDateDE(tip.date)}</p>
          <p className="font-mono text-amber-400">Arbeitszeit: {tip.work} h</p>
          <p className="font-mono text-cyan-400">Alle getrackten Stunden: {tip.total} h</p>
        </div>
      )}
      <div className="flex gap-[2px]" style={{ minWidth: Math.min(weeks.length * 12, 200) }}>
        {weeks.map((w, wi) => (
          <div key={wi} className="flex flex-col gap-[2px]">
            {w.map((d, di) => (
              <div
                key={di}
                className="w-[10px] h-[10px] rounded-[2px]"
                style={{ backgroundColor: d ? heatColor(d.work) : "transparent" }}
                onMouseEnter={(e) => d && setTip({ date: d.date, total: d.total, work: d.work, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTip(null)}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-[3px] mt-2 justify-end">
        <span className="text-[9px] text-zinc-600 mr-1">0 h</span>
        {HC.map((c, i) => (
          <div key={i} className="w-[10px] h-[10px] rounded-[2px]" style={{ backgroundColor: c }} />
        ))}
        <span className="text-[9px] text-zinc-600 ml-1">10+ h</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════ Chart Wrapper ═══════════════════════════ */

function ScrollChart({ count, height = 192, display, children }) {
  const ref = useRef(null);
  const wide = display === "days" ? count > 30 : count > 16;
  const barW = display === "days" ? 22 : 50;
  const onRef = useCallback((node) => {
    ref.current = node;
    if (node) node.scrollLeft = node.scrollWidth;
  }, [count]);
  return (
    <div className="overflow-x-auto" style={{ height }} ref={onRef}>
      <div style={{ width: wide ? count * barW : "100%", minWidth: "100%", height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ═══════════════════════════ Category Filter ═══════════════════════════ */

const CAT_FILTERS = [
  { key: "Produktion", color: "#F97316" },
  { key: "Administration", color: "#EF4444" },
  { key: "Akquise", color: "#EAB308" },
  { key: "Gesundheit", color: "#3B82F6" },
  { key: "Ordnung", color: "#8B5CF6" },
  { key: "Weiterbildung", color: "#10B981" },
];
const CAT_FILTER_KEYS = CAT_FILTERS.map((c) => c.key);

/* ═══════════════════════════ Page ═══════════════════════════ */

export default function StatistikenPage() {
  const [allEntries, setAllEntries] = useState([]);
  const [euroRate, setEuroRate] = useState(0.6);
  const [loading, setLoading] = useState(true);

  const [filterMode, setFilterMode] = useState("8weeks");
  const [customFrom, setCustomFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [customTo, setCustomTo] = useState(() => toStr(new Date()));
  const [displayMode, setDisplayMode] = useState("auto");
  const [activeCats, setActiveCats] = useState(() => new Set(CAT_FILTER_KEYS));

  useEffect(() => {
    Promise.all([api.get("/dashboard"), api.get("/entries")])
      .then(([dR, eR]) => {
        setEuroRate(dR.data.point_value_euro || 0.6);
        setAllEntries(eR.data.sort((a, b) => a.date.localeCompare(b.date)));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const range = useMemo(
    () => getFilterRange(filterMode, customFrom, customTo),
    [filterMode, customFrom, customTo]
  );

  const effDisplay = useMemo(
    () => (displayMode === "auto" ? getAutoDisplay(range.from, range.to) : displayMode),
    [displayMode, range]
  );

  const filtered = useMemo(
    () => allEntries.filter((e) => e.date >= range.from && e.date <= range.to),
    [allEntries, range]
  );

  const hourHeatmap = useMemo(() => {
    const m = {};
    filtered.forEach((e) => {
      const t = e.time_entries || {};
      const total = Math.round(Object.values(t).reduce((s, v) => s + (v || 0), 0) * 10) / 10;
      const work = Math.round(((t.produktion || 0) + (t.administration || 0) + (t.recherche || 0) + (t.cold_calls || 0)) * 10) / 10;
      m[e.date] = { total, work };
    });
    return m;
  }, [filtered]);

  const agg = useMemo(
    () => aggregateData(filtered, effDisplay, euroRate),
    [filtered, effDisplay, euroRate]
  );

  const catData = useMemo(() => {
    let prod = 0, adm = 0, akq = 0, ges = 0, ord = 0, wb = 0;
    filtered.forEach((e) => {
      const t = e.time_entries || {};
      prod += t.produktion || 0;
      adm += t.administration || 0;
      akq += (t.recherche || 0) + (t.cold_calls || 0);
      ges += t.gesundheit || 0;
      ord += t.ordnung || 0;
      wb += t.lernen || 0;
    });
    return [
      { name: "Produktion", value: Math.round(prod * 10) / 10, color: "#F97316" },
      { name: "Administration", value: Math.round(adm * 10) / 10, color: "#EF4444" },
      { name: "Akquise", value: Math.round(akq * 10) / 10, color: "#EAB308" },
      { name: "Gesundheit", value: Math.round(ges * 10) / 10, color: "#3B82F6" },
      { name: "Ordnung", value: Math.round(ord * 10) / 10, color: "#8B5CF6" },
      { name: "Weiterbildung", value: Math.round(wb * 10) / 10, color: "#10B981" },
    ].filter((c) => c.value > 0);
  }, [filtered]);

  const visibleCatData = useMemo(
    () => catData.filter((c) => activeCats.has(c.name)),
    [catData, activeCats]
  );
  const catTotal = visibleCatData.reduce((s, c) => s + c.value, 0);
  const rangeValid = range.from <= range.to;

  if (loading) {
    return (
      <div className="p-4 md:p-8 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-48 bg-zinc-900/50 animate-pulse" />
        ))}
      </div>
    );
  }

  const ax = { fill: "#52525B", fontSize: 10 };
  const pPrefix = PERIOD_PREFIX[effDisplay] || "Wochen";
  const pPer = PERIOD_PER[effDisplay] || "pro Woche";

  return (
    <div className="p-4 md:p-8 space-y-5 max-w-7xl mx-auto" data-testid="statistiken-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
          Statistiken
        </h1>
        <p className="text-zinc-500 text-sm mt-1" data-testid="range-display">
          {formatDateDE(range.from)} – {formatDateDE(range.to)}
        </p>
      </div>

      {/* ── Global Filter ── */}
      <Card className="bg-card border-border" data-testid="global-filter">
        <CardContent className="p-3 space-y-2.5">
          {/* Quick filter: always dropdown */}
          <div className="flex flex-wrap items-center gap-2" data-testid="quick-filters">
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider shrink-0">Zeitraum</span>
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              className="h-8 bg-zinc-950 border border-zinc-800 text-xs text-zinc-300 px-2 focus:outline-none focus:border-zinc-600"
              data-testid="filter-select"
            >
              {QUICK_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
            {/* Display mode: always dropdown */}
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider shrink-0 ml-2">Darstellung</span>
            <select
              value={displayMode}
              onChange={(e) => setDisplayMode(e.target.value)}
              className="h-8 bg-zinc-950 border border-zinc-800 text-xs text-zinc-300 px-2 focus:outline-none focus:border-zinc-600"
              data-testid="display-select"
            >
              {DISPLAY_MODES.map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
            {displayMode === "auto" && (
              <span className="text-[9px] text-zinc-700">
                ({DISPLAY_LABELS[effDisplay] || effDisplay})
              </span>
            )}
          </div>

          {/* Custom date inputs */}
          {filterMode === "custom" && (
            <div className="flex flex-wrap items-center gap-2" data-testid="custom-range">
              <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Von</span>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-9 bg-zinc-950/50 border border-zinc-800 text-xs text-zinc-400 font-mono px-2 focus:outline-none focus:border-zinc-600"
                data-testid="custom-from"
              />
              <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Bis</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-9 bg-zinc-950/50 border border-zinc-800 text-xs text-zinc-400 font-mono px-2 focus:outline-none focus:border-zinc-600"
                data-testid="custom-to"
              />
              {!rangeValid && (
                <span className="text-xs text-red-400" data-testid="range-error">
                  Von-Datum darf nicht nach Bis-Datum liegen
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {rangeValid && (
        <>
          {/* ── Tagesaktivitaet ── */}
          <Card className="bg-card border-border">
            <CardContent className="p-5">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
                Tagesaktivitaet
              </p>
              <p className="text-[9px] text-zinc-700 mb-4">Alle getrackten Stunden pro Tag</p>
              <Heatmap data={hourHeatmap} from={range.from} to={range.to} />
            </CardContent>
          </Card>

          {/* ── Arbeitszeit ── */}
          <Card className="bg-card border-border">
            <CardContent className="p-5">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
                {pPrefix}arbeitszeit
              </p>
              <p className="text-[9px] text-zinc-700 mb-4">Produktion + Administration + Akquise</p>
              <ScrollChart count={agg.length} height={208} display={effDisplay}>
                <BarChart data={agg}>
                  <XAxis dataKey="label" tick={ax} axisLine={false} tickLine={false} />
                  <YAxis tick={ax} axisLine={false} tickLine={false} />
                  <Tooltip content={<HoursTip />} />
                  <Bar dataKey="Produktion" stackId="w" fill="#F97316" name="Produktion" />
                  <Bar dataKey="Administration" stackId="w" fill="#EF4444" name="Administration" />
                  <Bar dataKey="Akquise" stackId="w" fill="#EAB308" name="Akquise" />
                </BarChart>
              </ScrollChart>
              <div className="flex items-center gap-4 mt-2 justify-center">
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-2 h-2 bg-orange-500" />
                  <span className="text-zinc-500">Produktion</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-2 h-2 bg-red-500" />
                  <span className="text-zinc-500">Administration</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className="w-2 h-2 bg-yellow-500" />
                  <span className="text-zinc-500">Akquise</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Punkteverlauf + Vergleich ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                  Punkteverlauf
                </p>
                <ScrollChart count={agg.length} display={effDisplay}>
                  <BarChart data={agg}>
                    <XAxis dataKey="label" tick={ax} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip content={<PtsTip />} />
                    <Bar dataKey="Zeit" stackId="p" fill="#38BDF8" name="Zeit" />
                    <Bar dataKey="Vertrieb" stackId="p" fill="#10B981" name="Vertrieb" />
                  </BarChart>
                </ScrollChart>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                  {pPrefix}vergleich
                </p>
                <ScrollChart count={agg.length} display={effDisplay}>
                  <BarChart data={agg}>
                    <XAxis dataKey="label" tick={ax} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip content={<PtsTip />} />
                    <Bar dataKey="Punkte" fill="#38BDF8" name="Punkte" />
                  </BarChart>
                </ScrollChart>
              </CardContent>
            </Card>
          </div>

          {/* ── Kategorienverteilung + Euro ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">
                  Kategorienverteilung
                </p>
                <div className="flex flex-wrap gap-1.5 mb-4" data-testid="cat-filter-chips">
                  {CAT_FILTERS.map((cf) => {
                    const on = activeCats.has(cf.key);
                    return (
                      <button
                        key={cf.key}
                        onClick={() => setActiveCats((prev) => {
                          const next = new Set(prev);
                          if (next.has(cf.key)) { if (next.size > 1) next.delete(cf.key); }
                          else next.add(cf.key);
                          return next;
                        })}
                        className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] border transition-colors"
                        style={{
                          borderColor: on ? cf.color : "rgba(63,63,70,0.4)",
                          backgroundColor: on ? `${cf.color}18` : "transparent",
                          color: on ? cf.color : "#52525B",
                        }}
                        data-testid={`cat-chip-${cf.key.toLowerCase()}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: on ? cf.color : "#3F3F46" }}
                        />
                        {cf.key}
                      </button>
                    );
                  })}
                </div>
                {visibleCatData.length > 0 ? (
                  <>
                    <div className="flex items-center h-44">
                      <ResponsiveContainer width="50%" height="100%">
                        <PieChart>
                          <Pie
                            data={visibleCatData}
                            cx="50%"
                            cy="50%"
                            innerRadius={38}
                            outerRadius={62}
                            dataKey="value"
                            stroke="none"
                          >
                            {visibleCatData.map((c, i) => (
                              <Cell key={i} fill={c.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CatTip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-1.5 flex-1">
                        {visibleCatData.map((c, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <div
                              className="w-2 h-2 flex-shrink-0"
                              style={{ backgroundColor: c.color }}
                            />
                            <span className="text-zinc-500 truncate">{c.name}</span>
                            <span className="font-mono text-zinc-400 ml-auto">
                              {formatNumber(c.value)} h
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-[9px] text-zinc-700 mt-2 text-right">
                      Gesamt: {formatNumber(Math.round(catTotal * 10) / 10)} h
                    </p>
                  </>
                ) : (
                  <p className="text-zinc-600 text-sm py-8 text-center">Keine Daten im Zeitraum</p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
                  Euro-Wert {pPer}
                </p>
                <ScrollChart count={agg.length} display={effDisplay}>
                  <BarChart data={agg}>
                    <XAxis dataKey="label" tick={ax} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip content={<EuroTip />} />
                    <Bar dataKey="euro" fill="#10B981" name="euro" />
                  </BarChart>
                </ScrollChart>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
